import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clipboard,
  Loader2,
  Search,
  Sparkles,
  Wand2
} from "lucide-react";
import { FormEvent, useMemo, useRef, useState } from "react";
import { analyzeGerman } from "./api";
import { ApiDebugError, type ApiDebugInfo, type AnalysisResult, type Gender, type PartOfSpeech } from "./types";

const quickQueries = ["дом", "das Mädchen", "lief", "Hauser", "to run"];

const partLabels: Record<PartOfSpeech, string> = {
  noun: "Nomen",
  verb: "Verb",
  adjective: "Adjektiv",
  adverb: "Adverb",
  pronoun: "Pronomen",
  preposition: "Präposition",
  conjunction: "Konjunktion",
  phrase: "Ausdruck",
  other: "Wort"
};

const genderLabels: Record<Exclude<Gender, null>, string> = {
  masculine: "maskulin",
  feminine: "feminin",
  neuter: "neutral",
  plural: "Plural"
};

function genderClass(gender: Gender | undefined) {
  return gender ? `gender-${gender}` : "gender-neutral";
}

export default function App() {
  const [query, setQuery] = useState("Mädchen");
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiDebugInfo | null>(null);
  const requestNumberRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const canSearch = query.trim().length > 0 && !isLoading;

  async function submit(nextQuery = query) {
    const trimmed = nextQuery.trim();
    if (!trimmed) return;

    const requestNumber = requestNumberRef.current + 1;
    requestNumberRef.current = requestNumber;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setQuery(trimmed);
    setIsLoading(true);
    setError(null);

    try {
      const started = performance.now();
      const analysis = await analyzeGerman(trimmed, controller.signal);
      if (requestNumber !== requestNumberRef.current) return;
      setResult({ ...analysis, responseTimeMs: Math.round(performance.now() - started) });
    } catch (error) {
      if (requestNumber !== requestNumberRef.current || controller.signal.aborted) return;
      setError(toDebugInfo(error));
    } finally {
      if (requestNumber === requestNumberRef.current) {
        setIsLoading(false);
      }
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit();
  }

  const visibleResult = useMemo(() => result, [result]);

  return (
    <main className="app-shell">
      <section className="search-panel" aria-labelledby="app-title">
        <div className="brand-row">
          <span className="brand-mark" aria-hidden="true">
            <BookOpen size={18} />
          </span>
          <span className="brand-name">DE Helper</span>
        </div>

        <div className="intro-copy">
          <h1 id="app-title">Deutsch verstehen, Form für Form</h1>
          <p>
            Schreib ein Wort oder eine kurze Phrase in einer beliebigen Sprache. Die Antwort kommt auf
            Deutsch: Artikel, Genus, Grundform, Verbformen, Korrektur und Beispiele.
          </p>
        </div>

        <form className="search-form" onSubmit={handleSubmit}>
          <label className="search-label" htmlFor="query">
            Wort, Übersetzung oder Phrase
          </label>
          <div className="search-box">
            <Search className="search-icon" size={20} aria-hidden="true" />
            <input
              id="query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="z. B. дом, gelaufen, Häuser, house"
              autoComplete="off"
              autoCapitalize="none"
            />
            <button className="icon-submit" type="submit" disabled={!canSearch} aria-label="Analysieren">
              {isLoading ? <Loader2 className="spin" size={20} /> : <ArrowRight size={20} />}
            </button>
          </div>
        </form>

        <div className="quick-row" aria-label="Schnelle Beispiele">
          {quickQueries.map((item) => (
            <button key={item} type="button" onClick={() => void submit(item)}>
              {item}
            </button>
          ))}
        </div>
      </section>

      {error && <DebugNotice error={error} />}

      {!visibleResult && !isLoading && (
        <section className="empty-state">
          <Sparkles size={20} />
          <span>Starte mit einem Wort in irgendeiner Sprache. Die Lernantwort bleibt auf Deutsch.</span>
        </section>
      )}

      {isLoading && <LoadingResult />}

      {visibleResult && !isLoading && <ResultView result={visibleResult} />}
    </main>
  );
}

function DebugNotice({ error }: { error: ApiDebugInfo }) {
  return (
    <section className="debug-card" role="alert">
      <div className="debug-heading">
        <AlertCircle size={18} />
        <div>
          <strong>{error.message}</strong>
          <span>{error.code}</span>
        </div>
      </div>

      <dl>
        {error.status && (
          <div>
            <dt>HTTP</dt>
            <dd>{error.status}</dd>
          </div>
        )}
        {error.model && (
          <div>
            <dt>Modell</dt>
            <dd>{error.model}</dd>
          </div>
        )}
        {typeof error.aiBinding === "boolean" && (
          <div>
            <dt>AI Binding</dt>
            <dd>{error.aiBinding ? "verbunden" : "fehlt"}</dd>
          </div>
        )}
        {error.requestId && (
          <div>
            <dt>Request ID</dt>
            <dd>{error.requestId}</dd>
          </div>
        )}
      </dl>

      {error.details && <p>{error.details}</p>}
      {Boolean(error.attemptedModels?.length) && (
        <div className="attempt-list">
          <span>Versuchte Modelle</span>
          {error.attemptedModels?.map((attempt) => (
            <code key={attempt.model}>
              {attempt.ok ? "OK" : "FAIL"} {attempt.model}
              {attempt.error ? `: ${attempt.error}` : ""}
            </code>
          ))}
        </div>
      )}
      {error.fallback && (
        <div className="fallback-debug">
          <span>Fallback vorhanden</span>
          <strong>
            {error.fallback.article ? `${error.fallback.article} ` : ""}
            {error.fallback.displayedTerm || error.fallback.lemma}
          </strong>
          <p>{error.fallback.shortMeaning}</p>
        </div>
      )}
    </section>
  );
}

function toDebugInfo(error: unknown): ApiDebugInfo {
  if (error instanceof ApiDebugError) {
    return error.info;
  }

  return {
    code: "UNKNOWN_CLIENT_ERROR",
    message: "Ein unbekannter Fehler ist im Browser aufgetreten.",
    details: error instanceof Error ? error.message : String(error)
  };
}

function LoadingResult() {
  return (
    <section className="result-grid loading-grid" aria-live="polite">
      <div className="skeleton hero-skeleton" />
      <div className="skeleton" />
      <div className="skeleton" />
    </section>
  );
}

function ResultView({ result }: { result: AnalysisResult }) {
  const hasCorrection = Boolean(result.correctedQuery && result.correctedQuery !== result.query);
  const mainTerm = getMainTerm(result);

  return (
    <section className="result-grid" aria-live="polite">
      <article className={`term-card ${genderClass(result.gender)}`}>
        <div className="card-topline">
          <span className="pill">{partLabels[result.partOfSpeech] ?? "слово"}</span>
          <span className="confidence">{confidenceLabel(result.confidence)}</span>
        </div>

        <div className="term-heading">
          {result.article && <span className="article-chip">{result.article}</span>}
          <h2>{mainTerm}</h2>
        </div>

        <p className="translation">{result.translation}</p>
        <p className="meaning">{result.shortMeaning}</p>

        {hasCorrection && (
          <div className="correction">
            <Wand2 size={17} />
            <span>
              Korrigiert: <b>{result.query}</b> {"->"} <b>{result.correctedQuery}</b>
              {result.correctionNote ? `. ${result.correctionNote}` : ""}
            </span>
          </div>
        )}

        {result.source === "demo" && (
          <div className="notice inline">
            <Sparkles size={16} />
            <span>Demo-Antwort. Mit dem Cloudflare-AI-Binding kommt hier die Live-Analyse.</span>
          </div>
        )}

        {result.modelUsed && (
          <div className="notice inline">
            <Sparkles size={16} />
            <span>AI-Modell: {result.modelUsed}</span>
          </div>
        )}

        {result.cacheHit && (
          <div className="notice inline">
            <CheckCircle2 size={16} />
            <span>Gespeicherte Antwort ({result.cacheLayer === "browser" ? "Browser" : "Cloudflare"})</span>
          </div>
        )}

        {typeof result.responseTimeMs === "number" && (
          <div className="notice inline">
            <CheckCircle2 size={16} />
            <span>Antwortzeit: {formatResponseTime(result.responseTimeMs)}</span>
          </div>
        )}
      </article>

      <article className="details-card">
        <h3>Grammatik</h3>
        <div className="facts">
          {result.gender && (
            <Fact label="Genus" value={genderLabels[result.gender]} tone={genderClass(result.gender)} />
          )}
          {result.plural && <Fact label="Plural" value={result.plural} />}
          {result.nounCaseHint && <Fact label="Hinweis" value={result.nounCaseHint} />}
          {result.lemma && <Fact label="Grundform" value={result.lemma} />}
        </div>

        {result.verbForms && (
          <div className="verb-table">
            <h4>Verbformen</h4>
            <dl>
              <div>
                <dt>Infinitiv</dt>
                <dd>{result.verbForms.infinitive}</dd>
              </div>
              {result.verbForms.present3rdPerson && (
                <div>
                  <dt>3. Person</dt>
                  <dd>{result.verbForms.present3rdPerson}</dd>
                </div>
              )}
              {result.verbForms.preterite && (
                <div>
                  <dt>Präteritum</dt>
                  <dd>{result.verbForms.preterite}</dd>
                </div>
              )}
              {result.verbForms.perfect && (
                <div>
                  <dt>Perfekt</dt>
                  <dd>{result.verbForms.perfect}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {Boolean(result.grammarNotes.length) && (
          <ul className="note-list">
            {result.grammarNotes.map((note) => (
              <li key={note}>
                <CheckCircle2 size={16} />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        )}
      </article>

      <article className="examples-card">
        <h3>Beispiele</h3>
        <div className="examples-list">
          {result.examples.map((example) => (
            <figure key={example.de}>
              <blockquote>{example.de}</blockquote>
              <figcaption>{example.explanation}</figcaption>
            </figure>
          ))}
        </div>
      </article>

      {Boolean(result.related.length) && (
        <article className="related-card">
          <h3>Verwandte Wörter</h3>
          <div className="related-list">
            {result.related.map((item) => (
              <button key={item} type="button" onClick={() => navigator.clipboard?.writeText(item)}>
                <Clipboard size={14} />
                <span>{item}</span>
              </button>
            ))}
          </div>
        </article>
      )}
    </section>
  );
}

function formatResponseTime(milliseconds: number) {
  if (milliseconds < 1000) return `${milliseconds} ms`;
  return `${(milliseconds / 1000).toFixed(1)} s`;
}

function getMainTerm(result: AnalysisResult) {
  const term = result.displayedTerm || result.lemma;
  if (!result.article) return term;

  const prefix = `${result.article} `;
  return term.toLocaleLowerCase("de-DE").startsWith(prefix)
    ? term.slice(prefix.length)
    : term;
}

function Fact({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className={`fact ${tone ?? ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function confidenceLabel(confidence: AnalysisResult["confidence"]) {
  if (confidence === "high") return "sicher";
  if (confidence === "medium") return "prüfen";
  return "unsicher";
}
